// LeadOpportunityPanel — Phase 5.
//
// Financial view of the lead — what this relationship is worth
// end-to-end, not just today's quote. Four numbers:
//   Current opportunity  — today's expected commission
//   Cross-sell value     — sum of recommendation commissions
//   Referral value       — projected referral revenue if they refer 1
//   Lifetime value       — sum of the above × retention multiplier
//
// Pulls today's expected_commission from /api/leads/command-center
// (already cached by the header) via a second fetch. Cross-sell +
// referral share the /api/leads/recommendations output. LTV is
// deterministic: (current + cross_sell + referral) × 2.5 retention
// multiplier — same simple heuristic the daily-value email uses.

import React from 'react';
import { readPersistedJwtDirect } from './authedFetch';
import { supabase } from '../lib/supabase';

async function bearer() {
  const token = readPersistedJwtDirect();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function fmt$(cents) {
  if (!Number.isFinite(cents) || cents === 0) return '$0';
  return `$${(cents / 100).toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
}

const LTV_RETENTION_MULT = 2.5;

export default function LeadOpportunityPanel({ lead }) {
  const [state, setState] = React.useState({ loading: true, current: 0, crossSell: 0, error: null });

  React.useEffect(() => {
    const controller = new AbortController();
    (async () => {
      const { authedFetchLead } = await import('./authedFetch');
      const [ccRes, recRes] = await Promise.all([
        authedFetchLead('/api/leads/command-center',  { lead }, controller.signal),
        authedFetchLead('/api/leads/recommendations', { lead }, controller.signal),
      ]);
      if (controller.signal.aborted) return;
      const current = ccRes.ok ? (ccRes.data.expected_commission_cents || 0) : 0;
      const recs = recRes.ok ? (recRes.data.recommendations || []) : [];
      const crossSell = recs.filter(r => r.priority >= 2).reduce((s, r) => s + (r.estimated_commission_cents || 0), 0);
      const err = (!ccRes.ok && !recRes.ok) ? (ccRes.error || recRes.error) : null;
      setState({ loading: false, current, crossSell, error: err });
    })();
    return () => { controller.abort(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lead?.id]);

  if (state.loading) return null;
  if (state.error) {
    return (
      <div style={{
        background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 10,
        padding: 10, fontSize: 11, color: '#92400e', marginBottom: 8,
      }}>
        Opportunity Tracker temporarily unavailable ({String(state.error)}).
      </div>
    );
  }

  const referralValue = Math.round((state.current + state.crossSell) * 0.3);
  const ltv = Math.round((state.current + state.crossSell + referralValue) * LTV_RETENTION_MULT);

  return (
    <div style={{
      background: '#f0fdf4',
      border: '1px solid #bbf7d0',
      borderRadius: 10, padding: 12,
    }}>
      <div style={{ fontSize: 10, letterSpacing: '0.15em', textTransform: 'uppercase', color: '#166534', fontWeight: 800, marginBottom: 10 }}>
        Opportunity Tracker
      </div>
      <div style={{
        display: 'grid', gap: 8,
        gridTemplateColumns: 'repeat(auto-fit, minmax(115px, 1fr))',
      }}>
        <Tile label="Current opp"       value={fmt$(state.current)}    tone="#059669"/>
        <Tile label="Cross-sell value"  value={fmt$(state.crossSell)}  tone="#0891b2"/>
        <Tile label="Referral value"    value={fmt$(referralValue)}    tone="#a16207"/>
        <Tile label="Lifetime value"    value={fmt$(ltv)}              tone="#7c3aed" strong/>
      </div>
      <div style={{ fontSize: 10, color: '#64748b', marginTop: 8, fontStyle: 'italic' }}>
        LTV = (current + cross-sell + projected referral) × {LTV_RETENTION_MULT} retention multiplier.
      </div>
    </div>
  );
}

function Tile({ label, value, tone, strong }) {
  return (
    <div style={{
      background: '#ffffff',
      border: `1px solid ${strong ? tone : '#d1fae5'}`,
      borderRadius: 6, padding: '6px 8px',
    }}>
      <div style={{ fontSize: 9, color: '#166534', letterSpacing: '0.1em', textTransform: 'uppercase', fontWeight: 700 }}>{label}</div>
      <div style={{
        fontSize: strong ? 20 : 17, fontWeight: 800, color: tone,
        fontFamily: 'JetBrains Mono, monospace', fontVariantNumeric: 'tabular-nums', marginTop: 2,
      }}>{value}</div>
    </div>
  );
}
